import axios from "axios";
import useSWRImmutable from "swr/immutable";

const GEOCODE_URL = "https://data.geopf.fr/geocodage/search";
// Jeu de données ADEME « DPE Logements existants (depuis juillet 2021) » : les DPE antérieurs
// ne sont plus valables depuis le 1er janvier 2025.
const ADEME_DATASET_URL =
  "https://data.ademe.fr/data-fair/api/v1/datasets/meg-83tjwtg8dyz4vv7h1dqe/lines";
const ADEME_FIELDS = [
  "numero_dpe",
  "etiquette_dpe",
  "etiquette_ges",
  "surface_habitable_logement",
  "date_etablissement_dpe",
  "date_fin_validite_dpe",
  "complement_adresse_batiment",
  "complement_adresse_logement",
  "type_batiment",
  "annee_construction",
  "periode_construction",
  "type_installation_chauffage",
  "type_installation_ecs",
  "type_energie_principale_chauffage",
  "type_energie_principale_ecs",
  "type_energie_n1",
  "type_energie_n2",
  "type_energie_n3",
  "adresse_ban",
];
const MAX_RESULTS = 30;

/** DPE de la base de l'observatoire ADEME, tel que renvoyé par l'API data-fair. */
export interface AdemeDpe {
  numero_dpe: string;
  etiquette_dpe: string;
  etiquette_ges?: string;
  surface_habitable_logement?: number;
  date_etablissement_dpe?: string;
  date_fin_validite_dpe?: string;
  complement_adresse_batiment?: string;
  complement_adresse_logement?: string;
  type_batiment?: string;
  annee_construction?: number;
  periode_construction?: string;
  /** « individuel », « collectif » ou « mixte ». */
  type_installation_chauffage?: string;
  type_installation_ecs?: string;
  /** Énergies du logement : « Électricité », « Gaz naturel », « GPL », « Fioul domestique »… */
  type_energie_principale_chauffage?: string;
  type_energie_principale_ecs?: string;
  type_energie_n1?: string;
  type_energie_n2?: string;
  type_energie_n3?: string;
  adresse_ban?: string;
}

/** DPE de l'observatoire portant ce numéro (13 caractères figurant sur le document), null s'il est inconnu. */
export async function fetchDpeByNumber(
  numero: string,
): Promise<AdemeDpe | null> {
  const { data } = await axios.get<{ results: AdemeDpe[] }>(ADEME_DATASET_URL, {
    params: {
      qs: `numero_dpe:"${numero.trim().toUpperCase()}"`,
      select: ADEME_FIELDS.join(","),
      size: 1,
    },
  });
  return data.results[0] ?? null;
}

export type DpeLookupResult =
  /** L'adresse n'est pas localisée au numéro : impossible de la rapprocher des DPE. */
  { kind: "imprecise" } | { kind: "found"; address: string; dpes: AdemeDpe[] };

interface BanFeature {
  properties: { id: string; label: string; type: string };
}

/**
 * DPE enregistrés à l'ADEME pour une adresse : l'adresse est rapprochée de la
 * BAN (identifiant d'adresse) puis les DPE portant cet identifiant sont lus
 * dans l'observatoire, du plus récent au plus ancien. Interrogé seulement quand
 * `enabled` est vrai (aucun indice DPE encore renseigné).
 */
export default function useDpeLookup(
  { rue, cp, ville }: { rue: string; cp: string; ville: string },
  enabled: boolean,
) {
  const { data, error, isLoading } = useSWRImmutable(
    enabled && rue.trim() && cp.trim() && ville.trim()
      ? ["dpe-lookup", rue.trim(), cp.trim(), ville.trim()]
      : null,
    async ([, q, postcode, city]): Promise<DpeLookupResult> => {
      const geocoding = await axios.get<{ features: BanFeature[] }>(
        GEOCODE_URL,
        { params: { q: `${q} ${city}`, postcode, limit: 1 } },
      );
      const address = geocoding.data.features[0];
      if (!address || address.properties.type !== "housenumber") {
        return { kind: "imprecise" };
      }
      const { data: ademe } = await axios.get<{ results: AdemeDpe[] }>(
        ADEME_DATASET_URL,
        {
          params: {
            qs: `identifiant_ban:"${address.properties.id}"`,
            select: ADEME_FIELDS.join(","),
            sort: "-date_etablissement_dpe",
            size: MAX_RESULTS,
          },
        },
      );
      return {
        kind: "found",
        address: address.properties.label,
        dpes: ademe.results,
      };
    },
  );
  return { result: data, error, isLoading };
}
